import { Link } from "react-router-dom";
import type { Topic } from "@/mocks/guaData";
import { formatHeat, getCategoryBySlug, getTrendMeta } from "@/utils/content";

interface RankRowProps {
  topic: Topic;
  rank: number;
}

const rankStyles = [
  "bg-primary-500 text-background-50",
  "bg-primary-400 text-background-50",
  "bg-accent-500 text-foreground-950",
];

export default function RankRow({ topic, rank }: RankRowProps) {
  const category = getCategoryBySlug(topic.categorySlug);
  const trend = getTrendMeta(topic.trend);
  const rankClass = rankStyles[rank - 1] ?? "bg-background-200 text-foreground-700";

  return (
    <article className="flex items-start gap-3 rounded-lg border border-transparent px-3 py-3 transition-colors hover:border-background-200 hover:bg-background-100/80 md:items-center md:gap-4 md:px-4">
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm font-black ${rankClass}`}
        aria-label={`第 ${rank} 名`}
      >
        {rank}
      </span>

      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-bold leading-snug text-foreground-950 md:text-base">
          <Link to={`/topic/${topic.id}`} className="cursor-pointer transition-colors hover:text-primary-700">
            {topic.title}
          </Link>
        </h3>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
          {category && (
            <Link
              to={`/category/${category.slug}`}
              className="cursor-pointer text-secondary-700 transition-colors hover:text-secondary-900"
            >
              {category.name}
            </Link>
          )}
          <span className="hidden sm:inline">{topic.tags.slice(0, 2).map((tag) => `#${tag}`).join(" ")}</span>
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <span className="flex items-center gap-1 text-sm font-bold text-primary-600">
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-fire-line text-sm leading-none" aria-hidden="true" />
          </span>
          {formatHeat(topic.heat)}
        </span>
        <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${trend.className}`}>
          <span className="flex h-3 w-3 items-center justify-center">
            <i className={`${trend.icon} text-xs leading-none`} aria-hidden="true" />
          </span>
          {trend.text}
        </span>
      </div>
    </article>
  );
}